import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  Archive,
  CalendarClock,
  Clock,
  FileDown,
  Pencil,
  Tractor,
  Users,
} from "lucide-react";
import type { SiteDetail } from "@crewledger/shared/schemas";
import {
  CommandCenterCard,
  ConfirmationModal,
  EmptyState,
  PageHeader,
  SiteCommandCenter,
  StatCard,
  StatusBadge,
  useToast,
} from "@/components/design";
import { Button } from "@/components/ui/button";
import { formatCurrency, formatHours } from "@/features/dashboard/utils";
import { archiveSite, fetchSite } from "./api";
import { SiteStatusBadge } from "./components/site-status-badge";

function mapEntryStatus(status: string): "Draft" | "Submitted" | "Approved" | "Rejected" | "Locked" {
  switch (status) {
    case "SUBMITTED":
      return "Submitted";
    case "APPROVED":
      return "Approved";
    case "REJECTED":
      return "Rejected";
    case "LOCKED":
      return "Locked";
    default:
      return "Draft";
  }
}

export function SiteDetailPage() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [site, setSite] = useState<SiteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const load = () => {
    if (!siteId) return;
    setLoading(true);
    fetchSite(siteId)
      .then((res) => { setSite(res.data); })
      .catch((err: unknown) => { setError(err instanceof Error ? err.message : "Failed to load site"); })
      .finally(() => { setLoading(false); });
  };

  useEffect(load, [siteId]);

  const handleArchive = async () => {
    if (!siteId) return;
    setArchiving(true);
    try {
      await archiveSite(siteId, "Archived from site detail page");
      toast({ variant: "success", title: "Site archived", description: "The site has been closed." });
      setArchiveOpen(false);
      load();
    } catch (err) {
      toast({
        variant: "error",
        title: "Archive failed",
        description: err instanceof Error ? err.message : "Could not archive site",
      });
    } finally {
      setArchiving(false);
    }
  };

  if (error && !site) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="Site not found"
        description={error}
        actionLabel="Back to sites"
        onAction={() => navigate("/sites")}
      />
    );
  }

  if (loading || !site) {
    return <div className="h-96 animate-pulse rounded-xl border bg-card-white" />;
  }

  const { metrics } = site;

  return (
    <div className="space-y-6 lg:space-y-8">
      <PageHeader
        title={site.name}
        description={site.address ?? "No address on file"}
        badge={<SiteStatusBadge status={site.status} />}
        actions={
          site.canEdit && site.status !== "CLOSED" ? (
            <div className="flex flex-wrap gap-2">
              <Link to={`/sites/${site.id}/edit`}>
                <Button variant="outline" size="sm">
                  <Pencil className="mr-1.5 h-4 w-4" />
                  Edit
                </Button>
              </Link>
              <Button variant="destructive" size="sm" onClick={() => { setArchiveOpen(true); }}>
                <Archive className="mr-1.5 h-4 w-4" />
                Archive
              </Button>
            </div>
          ) : undefined
        }
      />

      <SiteCommandCenter
        siteName={site.name}
        siteCode={site.code}
        address={site.address ?? undefined}
        status={metrics.dailyEntries.some((e) => e.status === "SUBMITTED") ? "Submitted" : "Approved"}
        metrics={[
          { label: "Period hours", value: formatHours(metrics.hours.total) },
          { label: "OT hours", value: formatHours(metrics.hours.overtime) },
          { label: "Workers", value: metrics.activeWorkers },
          { label: "Exceptions", value: metrics.openExceptions },
        ]}
        alerts={
          metrics.openExceptions > 0
            ? [{ message: `${metrics.openExceptions} open exceptions need review`, severity: "warning" }]
            : []
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Regular hours" value={formatHours(metrics.hours.regular)} icon={Clock} accent="navy" />
        <StatCard label="Overtime" value={formatHours(metrics.hours.overtime)} icon={Clock} accent="amber" />
        <StatCard label="Double time" value={formatHours(metrics.hours.doubleTime)} icon={Clock} accent="amber" />
        <StatCard label="Equipment" value={metrics.equipmentAssigned} icon={Tractor} accent="green" />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <CommandCenterCard title="Today's entries" description={metrics.payPeriod?.label ?? "Current period"} icon={CalendarClock}>
            {metrics.dailyEntries.length === 0 ? (
              <EmptyState
                icon={Clock}
                title="No entries today"
                description="Daily labor entries will appear here once foremen log hours."
                className="border-0 bg-transparent py-4"
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="pb-2">Worker</th>
                      <th className="pb-2">Hours</th>
                      <th className="pb-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.dailyEntries.map((entry) => (
                      <tr key={entry.id} className="border-b border-border/60 last:border-0">
                        <td className="py-2 font-medium text-navy-deep">{entry.workerName}</td>
                        <td className="py-2 tabular-nums">{formatHours(entry.hours)}</td>
                        <td className="py-2">
                          <StatusBadge status={mapEntryStatus(entry.status)} showDot={false} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CommandCenterCard>

          <CommandCenterCard title="Cost by job code" description="Gross pay breakdown for current pay period">
            {metrics.costByJobCode.length === 0 ? (
              <p className="text-sm text-muted-foreground">No job code costs recorded yet.</p>
            ) : (
              <div className="space-y-2">
                {metrics.costByJobCode.map((row) => (
                  <div key={row.jobCode} className="flex items-center justify-between rounded-md bg-bg-light px-3 py-2 text-sm">
                    <span className="font-mono font-medium text-navy-deep">{row.jobCode}</span>
                    <span className="tabular-nums text-muted-foreground">
                      {formatHours(row.hours)}h · {formatCurrency(row.grossPay)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CommandCenterCard>
        </div>

        <div className="space-y-6">
          <CommandCenterCard title="Site details" icon={Users}>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">Pay rule profile</dt>
                <dd className="font-medium text-navy-deep">{site.defaultPayRuleProfileName ?? "None"}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">Managers</dt>
                <dd className="mt-1 space-y-1">
                  {site.managers.length === 0 ? (
                    <span className="text-muted-foreground">Unassigned</span>
                  ) : (
                    site.managers.map((m) => (
                      <div key={m.id} className="font-medium text-navy-deep">
                        {m.name}
                        <span className="ml-1 text-xs text-muted-foreground">{m.email}</span>
                      </div>
                    ))
                  )}
                </dd>
              </div>
              {site.notes ? (
                <div>
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">Notes</dt>
                  <dd className="whitespace-pre-wrap text-navy-deep">{site.notes}</dd>
                </div>
              ) : null}
            </dl>
          </CommandCenterCard>

          <CommandCenterCard title="Quick actions" variant={metrics.openExceptions > 0 ? "alert" : "default"}>
            <div className="grid gap-2">
              <Link to={`/daily-entry?siteId=${site.id}`}>
                <Button variant="outline" size="sm" className="w-full justify-start">
                  Daily entry
                </Button>
              </Link>
              <Link to={`/equipment?siteId=${site.id}`}>
                <Button variant="outline" size="sm" className="w-full justify-start">
                  View equipment
                </Button>
              </Link>
              <Link to="/pay-periods">
                <Button variant="outline" size="sm" className="w-full justify-start">
                  Review pay period
                </Button>
              </Link>
              <Link to="/exports">
                <Button variant="outline" size="sm" className="w-full justify-start">
                  <FileDown className="mr-1.5 h-4 w-4" />
                  Export payroll
                </Button>
              </Link>
            </div>
          </CommandCenterCard>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <CommandCenterCard title="Open exceptions" icon={AlertTriangle} variant={metrics.exceptions.length > 0 ? "alert" : "success"}>
          {metrics.exceptions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No open exceptions for this site.</p>
          ) : (
            <ul className="space-y-2">
              {metrics.exceptions.map((ex) => (
                <li key={ex.id} className="rounded-md border border-amber-200 bg-amber-50/50 px-3 py-2 text-sm">
                  <span className="font-medium text-navy-deep">{ex.type.replaceAll("_", " ")}</span>
                  <p className="text-muted-foreground">{ex.message}</p>
                </li>
              ))}
            </ul>
          )}
        </CommandCenterCard>

        <CommandCenterCard title="Recent audit activity">
          {metrics.auditActivity.length === 0 ? (
            <p className="text-sm text-muted-foreground">No audit events recorded for this site yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {metrics.auditActivity.map((log) => (
                <li key={log.id} className="border-b border-border/60 pb-2 last:border-0">
                  <p className="font-medium text-navy-deep">{log.action.replaceAll(".", " · ")}</p>
                  <p className="text-xs text-muted-foreground">
                    {log.actorName ?? "System"} · {new Date(log.createdAt).toLocaleString()}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </CommandCenterCard>
      </div>

      <ConfirmationModal
        open={archiveOpen}
        onClose={() => { setArchiveOpen(false); }}
        onConfirm={() => void handleArchive()}
        title="Archive this site?"
        description="Archived sites are closed to new time entries. Existing data is preserved for audit and payroll history."
        confirmLabel="Archive site"
        variant="destructive"
        loading={archiving}
      />
    </div>
  );
}
